import React, { useState, useEffect, useCallback } from 'react';
import BackButton from '@/components/BackButton';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '@/contexts/LanguageContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  House, PaperPlaneTilt, Bell, Megaphone, Users, ClockClockwise,
  CheckCircle, Info, Warning, XCircle, SpinnerGap, CaretDown, CaretRight, Paperclip,
} from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { fmtKsa } from '@/utils/formatKsa';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "/branding/logo.png";

/**
 * `fmtKsa` (imported above) formats UTC timestamps as Riyadh local time.
 */

const TYPE_META = {
  info:    { icon: Info, color: 'text-sky-700 bg-sky-50 border-sky-200', label_ar: 'معلومة', label_en: 'Info' },
  success: { icon: CheckCircle, color: 'text-emerald-700 bg-emerald-50 border-emerald-200', label_ar: 'نجاح', label_en: 'Success' },
  warning: { icon: Warning, color: 'text-amber-700 bg-amber-50 border-amber-200', label_ar: 'تحذير', label_en: 'Warning' },
  error:   { icon: XCircle, color: 'text-red-700 bg-red-50 border-red-200', label_ar: 'خطأ', label_en: 'Error' },
};

export default function Notifications() {
  const { t } = useTranslation();
  const { language, isRTL } = useLanguage();
  const navigate = useNavigate();
  const isAr = language === 'ar';
  const direction = isRTL ? 'rtl' : 'ltr';

  const [departments, setDepartments] = useState([]);
  const [audience, setAudience] = useState('all'); // all | departments | users
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [selectedDepts, setSelectedDepts] = useState(new Set());
  const [expandedDepts, setExpandedDepts] = useState(new Set());

  const [titleAr, setTitleAr] = useState('');
  const [titleEn, setTitleEn] = useState('');
  const [bodyAr, setBodyAr] = useState('');
  const [bodyEn, setBodyEn] = useState('');
  const [type, setType] = useState('info');
  const [alsoEmail, setAlsoEmail] = useState(false);
  // When the admin enables this, a 1×1 tracking pixel is injected into the
  // email's HTML body and the recipient's open is recorded against this
  // notification's log entry. Visible in the "تتبع الإيميلات" tab.
  const [trackOpens, setTrackOpens] = useState(false);
  // File attachments (PDFs / images) — only sent when alsoEmail is true.
  // Soft caps mirror the backend: ≤ 5 files, ≤ 20 MB total.
  const [attachFiles, setAttachFiles] = useState([]);

  const [sending, setSending] = useState(false);
  const [logs, setLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  // Tracked-emails tab data (loaded lazily when the tab is opened).
  const [tracking, setTracking] = useState([]);
  const [loadingTracking, setLoadingTracking] = useState(false);

  const fetchDepartments = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/notifications/employees-by-department`, { withCredentials: true });
      setDepartments(data);
      setExpandedDepts(new Set(data.map((d) => d.department)));
    } catch (err) {
      console.error(err);
    }
  }, []);

  const fetchLogs = useCallback(async () => {
    setLoadingLogs(true);
    try {
      const { data } = await axios.get(`${API}/in-app-notifications/broadcast/log`, { withCredentials: true });
      setLogs(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingLogs(false);
    }
  }, []);

  const fetchTracking = useCallback(async () => {
    setLoadingTracking(true);
    try {
      const { data } = await axios.get(`${API}/notifications/tracking`, { withCredentials: true });
      setTracking(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingTracking(false);
    }
  }, []);

  useEffect(() => {
    fetchDepartments();
    fetchLogs();
  }, [fetchDepartments, fetchLogs]);

  const totalEmployees = departments.reduce((sum, d) => sum + d.employees.length, 0);

  const toggleEmployee = (id) => {
    setSelectedIds((p) => {
      const next = new Set(p);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleDept = (dept) => {
    const ids = (departments.find((d) => d.department === dept)?.employees || []).map((e) => e.id);
    setSelectedIds((p) => {
      const next = new Set(p);
      const allIn = ids.every((id) => next.has(id));
      if (allIn) ids.forEach((id) => next.delete(id));
      else ids.forEach((id) => next.add(id));
      return next;
    });
    setSelectedDepts((p) => {
      const next = new Set(p);
      if (next.has(dept)) next.delete(dept); else next.add(dept);
      return next;
    });
  };

  const toggleExpand = (dept) => {
    setExpandedDepts((p) => {
      const next = new Set(p);
      if (next.has(dept)) next.delete(dept); else next.add(dept);
      return next;
    });
  };

  const recipientCount = audience === 'all'
    ? totalEmployees
    : audience === 'departments'
      ? departments.filter((d) => selectedDepts.has(d.department)).reduce((s, d) => s + d.employees.length, 0)
      : selectedIds.size;

  const handleSend = async () => {
    if (!titleAr.trim()) {
      toast.error(isAr ? 'الرجاء إدخال عنوان الإشعار' : 'Please enter a title');
      return;
    }
    if (audience === 'departments' && selectedDepts.size === 0) {
      toast.error(isAr ? 'اختر قسماً واحداً على الأقل' : 'Pick at least one department');
      return;
    }
    if (audience === 'users' && selectedIds.size === 0) {
      toast.error(isAr ? 'اختر موظفاً واحداً على الأقل' : 'Pick at least one employee');
      return;
    }

    setSending(true);
    try {
      const payload = {
        title_ar: titleAr.trim(),
        title_en: titleEn.trim() || titleAr.trim(),
        body_ar: bodyAr.trim(),
        body_en: bodyEn.trim() || bodyAr.trim(),
        type,
        category: 'announcement',
        audience,
        departments: audience === 'departments' ? Array.from(selectedDepts) : null,
        user_ids: audience === 'users' ? Array.from(selectedIds) : null,
      };
      const { data } = await axios.post(`${API}/in-app-notifications/broadcast`, payload, { withCredentials: true });
      const inAppSent = data.sent || 0;

      // Optionally send the same announcement by email (transactional, branded).
      let emailMsg = '';
      if (alsoEmail) {
        // Resolve audience to a flat list of employee IDs the email endpoint
        // expects (it takes ids directly, not departments/all).
        const allEmployeeIds = departments.flatMap((d) => d.employees.map((e) => e.id));
        let ids;
        if (audience === 'all') ids = allEmployeeIds;
        else if (audience === 'departments') {
          ids = departments
            .filter((d) => selectedDepts.has(d.department))
            .flatMap((d) => d.employees.map((e) => e.id));
        } else ids = Array.from(selectedIds);

        if (ids.length > 0) {
          try {
            // FormData required for file upload. Server reads
            // `employee_ids` as a JSON-encoded string.
            const fd = new FormData();
            fd.append('employee_ids', JSON.stringify(ids));
            fd.append('subject_ar', titleAr.trim());
            fd.append('subject_en', titleEn.trim() || titleAr.trim());
            fd.append('body_ar', bodyAr.trim() || titleAr.trim());
            fd.append('body_en', bodyEn.trim() || (titleEn.trim() || titleAr.trim()));
            fd.append('track_opens', trackOpens ? 'true' : 'false');
            for (const file of attachFiles) {
              fd.append('attachments', file, file.name);
            }
            const emailRes = await axios.post(
              `${API}/notifications/send-bulk`,
              fd,
              {
                withCredentials: true,
                headers: { 'Content-Type': 'multipart/form-data' },
              },
            );
            const eSent = emailRes.data.sent || 0;
            const eFailed = emailRes.data.failed || 0;
            emailMsg = isAr
              ? ` · أُرسل ${eSent} إيميل${eFailed ? ` (فشل ${eFailed})` : ''}${attachFiles.length ? ` مع ${attachFiles.length} مرفق` : ''}`
              : ` · ${eSent} email${eSent !== 1 ? 's' : ''} sent${eFailed ? ` (${eFailed} failed)` : ''}${attachFiles.length ? ` with ${attachFiles.length} attachment(s)` : ''}`;
          } catch (emailErr) {
            console.error('Email send failed:', emailErr);
            emailMsg = isAr ? ' · تعذّر إرسال البريد' : ' · email send failed';
          }
        }
      }

      toast.success(
        isAr
          ? `تم إرسال الإشعار إلى ${inAppSent} موظف${emailMsg}`
          : `In-app sent to ${inAppSent} employee${inAppSent !== 1 ? 's' : ''}${emailMsg}`,
      );
      // reset form
      setTitleAr(''); setTitleEn(''); setBodyAr(''); setBodyEn('');
      setSelectedIds(new Set()); setSelectedDepts(new Set());
      setAlsoEmail(false);
      setAttachFiles([]);
      fetchLogs();
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || (isAr ? 'فشل الإرسال' : 'Send failed'));
    } finally {
      setSending(false);
    }
  };

  const TypeIcon = TYPE_META[type].icon;

  return (
    <div className="min-h-screen bg-[#F8F7FB]" dir={direction}>
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-sm shadow-sm border-b border-[#C4BFF0] py-4">
        <div className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          <img src={LOGO_URL} alt="Lucent" className="h-10 object-contain" />
          <BackButton className="text-[#655dc6]" />
        </div>
      </header>

      <main className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-3xl font-semibold text-[#655dc6] flex items-center gap-3">
              <Bell size={28} weight="duotone" />
              {isAr ? 'مركز الإشعارات الداخلية' : 'In-App Notification Center'}
            </h1>
            <p className="text-sm text-[#655dc6]/70 mt-2">
              {isAr
                ? 'إرسال إعلانات داخل التطبيق للموظفين — تظهر على جرس الإشعار في بوابتهم. إشعارات الرواتب والإجازات ترسَل تلقائياً عند حدوث الأحداث.'
                : 'Send in-app announcements to employees — they appear in their bell icon. Payroll/leave notifications fire automatically on events.'}
            </p>
          </div>
          <Button
            onClick={() => navigate('/admin/push-subscribers')}
            variant="outline"
            className="border-[#C4BFF0] text-[#655dc6] hover:bg-[#EDE9FE] gap-2 shrink-0"
            data-testid="btn-push-subscribers"
          >
            <Users size={16} weight="duotone" />
            {isAr ? 'مشتركو إشعارات الجوال' : 'Push subscribers'}
          </Button>
        </div>

        <Tabs defaultValue="send" className="w-full" dir={direction} onValueChange={(v) => { if (v === 'tracking') fetchTracking(); }}>
          <TabsList className="bg-white border border-[#655dc6]/10">
            <TabsTrigger value="send" className="data-[state=active]:bg-[#655dc6] data-[state=active]:text-white gap-2" data-testid="tab-send">
              <Megaphone size={16} weight="duotone" />
              {isAr ? 'إرسال إشعار' : 'Send announcement'}
            </TabsTrigger>
            <TabsTrigger value="logs" className="data-[state=active]:bg-[#655dc6] data-[state=active]:text-white gap-2" data-testid="tab-logs">
              <ClockClockwise size={16} weight="duotone" />
              {isAr ? 'سجل الإشعارات المرسلة' : 'Sent log'}
            </TabsTrigger>
            <TabsTrigger value="tracking" className="data-[state=active]:bg-[#655dc6] data-[state=active]:text-white gap-2" data-testid="tab-tracking">
              <CheckCircle size={16} weight="duotone" />
              {isAr ? 'تتبع الإيميلات' : 'Email tracking'}
            </TabsTrigger>
          </TabsList>

          {/* SEND tab */}
          <TabsContent value="send" className="space-y-4 mt-4">
            <Card className="border-[#C4BFF0]" data-testid="compose-card">
              <CardHeader>
                <CardTitle className="text-[#655dc6] text-base">{isAr ? 'صياغة الإعلان' : 'Compose announcement'}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-start block mb-1">{isAr ? 'العنوان (عربي) *' : 'Title (Arabic) *'}</Label>
                    <Input value={titleAr} onChange={(e) => setTitleAr(e.target.value)}
                      placeholder={isAr ? 'مثال: اجتماع عام يوم الأربعاء' : 'eg. General meeting on Wednesday'}
                      style={{ fontFamily: "'Readex Pro', 'Inter', 'Segoe UI', Tahoma, Arial, sans-serif", fontWeight: 300 }}
                      data-testid="title-ar" />
                  </div>
                  <div>
                    <Label className="text-xs text-start block mb-1">{isAr ? 'العنوان (إنجليزي)' : 'Title (English)'}</Label>
                    <Input value={titleEn} onChange={(e) => setTitleEn(e.target.value)} dir="ltr"
                      style={{ fontFamily: "'Readex Pro', 'Inter', 'Segoe UI', Tahoma, Arial, sans-serif", fontWeight: 300 }}
                      data-testid="title-en" />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-start block mb-1">{isAr ? 'النص (عربي)' : 'Body (Arabic)'}</Label>
                    <Textarea rows={4} value={bodyAr} onChange={(e) => setBodyAr(e.target.value)}
                      placeholder={isAr ? 'اكتب التفاصيل…' : 'Type the details…'}
                      style={{ fontFamily: "'Readex Pro', 'Inter', 'Segoe UI', Tahoma, Arial, sans-serif", fontWeight: 300 }}
                      data-testid="body-ar" />
                  </div>
                  <div>
                    <Label className="text-xs text-start block mb-1">{isAr ? 'النص (إنجليزي)' : 'Body (English)'}</Label>
                    <Textarea rows={4} value={bodyEn} onChange={(e) => setBodyEn(e.target.value)} dir="ltr"
                      style={{ fontFamily: "'Readex Pro', 'Inter', 'Segoe UI', Tahoma, Arial, sans-serif", fontWeight: 300 }}
                      data-testid="body-en" />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-start block mb-1">{isAr ? 'نوع الإشعار' : 'Notification type'}</Label>
                    <Select value={type} onValueChange={setType} dir={direction}>
                      <SelectTrigger className="bg-white" data-testid="type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(TYPE_META).map(([k, m]) => (
                          <SelectItem key={k} value={k}>
                            {isAr ? m.label_ar : m.label_en}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-end">
                    <div className={`w-full px-3 py-2 rounded-md border text-xs flex items-center gap-2 ${TYPE_META[type].color}`}>
                      <TypeIcon size={16} weight="duotone" />
                      {isAr ? 'هكذا سيظهر للموظف' : 'Preview color'}
                    </div>
                  </div>
                </div>

                {/* Channel toggle — also send via email */}
                <div className="border border-[#C4BFF0] rounded-lg bg-[#FBFAFE] px-3 py-2.5 flex items-start gap-3" data-testid="email-toggle-row">
                  <Checkbox
                    id="also-email"
                    checked={alsoEmail}
                    onCheckedChange={(v) => setAlsoEmail(!!v)}
                    className="mt-0.5"
                    data-testid="also-email-cb"
                  />
                  <div className="flex-1">
                    <Label htmlFor="also-email" className="text-sm font-semibold text-[#655dc6] cursor-pointer flex items-center gap-2">
                      <PaperPlaneTilt size={15} weight="duotone" />
                      {isAr ? 'أرسل بالبريد الإلكتروني أيضاً' : 'Also send by email'}
                    </Label>
                    <p className="text-[11px] text-[#655dc6]/70 mt-0.5 leading-relaxed">
                      {isAr
                        ? 'سيتلقى المستلمون نفس الإشعار في صندوق بريدهم بتصميم لوسنت الرسمي بالعربية والإنجليزية. مفيد للإعلانات المهمة.'
                        : 'Recipients will also receive a bilingual branded email in their inbox. Useful for important announcements.'}
                    </p>

                    {/* Attachments — only visible when email channel is on. */}
                    {alsoEmail && (
                      <div className="mt-3 p-3 rounded-md bg-white border border-dashed border-[#C4BFF0]" data-testid="email-attachments-row">
                        <Label htmlFor="attach-input" className="text-xs font-semibold text-[#655dc6] flex items-center gap-1.5 cursor-pointer">
                          <Paperclip size={14} weight="bold" />
                          {isAr ? 'مرفقات (خطاب رسمي، تذكرة سفر…)' : 'Attachments (letters, flight tickets…)'}
                        </Label>
                        <input
                          id="attach-input"
                          type="file"
                          multiple
                          accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.xls,.xlsx"
                          className="block w-full mt-2 text-xs file:me-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:bg-[#E8E6F5] file:text-[#655dc6] file:font-semibold hover:file:bg-[#D8D3F0] cursor-pointer"
                          onChange={(e) => {
                            const files = Array.from(e.target.files || []);
                            const MAX_TOTAL = 20 * 1024 * 1024;
                            const total = files.reduce((s, f) => s + f.size, 0);
                            if (files.length > 5) {
                              toast.error(isAr ? 'الحد الأقصى 5 ملفات' : 'Maximum 5 files');
                              e.target.value = '';
                              return;
                            }
                            if (total > MAX_TOTAL) {
                              toast.error(isAr ? 'الحجم الإجمالي يتجاوز 20 ميجابايت' : 'Total size exceeds 20 MB');
                              e.target.value = '';
                              return;
                            }
                            setAttachFiles(files);
                          }}
                          data-testid="attach-input"
                        />
                        {attachFiles.length > 0 && (
                          <ul className="mt-2 space-y-1">
                            {attachFiles.map((f, i) => (
                              <li
                                key={i}
                                className="text-[11px] text-[#655dc6]/80 flex items-center gap-2 bg-[#F8F7FB] rounded px-2 py-1"
                                dir="ltr"
                              >
                                <Paperclip size={11} className="shrink-0" />
                                <span className="flex-1 truncate" title={f.name}>{f.name}</span>
                                <span className="text-[10px] text-[#655dc6]/50 tabular-nums">
                                  {(f.size / 1024 / 1024).toFixed(2)} MB
                                </span>
                                <button
                                  type="button"
                                  onClick={() => setAttachFiles(prev => prev.filter((_, idx) => idx !== i))}
                                  className="text-red-500/70 hover:text-red-600 text-xs"
                                  data-testid={`remove-attach-${i}`}
                                >
                                  ✕
                                </button>
                              </li>
                            ))}
                          </ul>
                        )}
                        <p className="text-[10px] text-[#655dc6]/40 mt-1.5">
                          {isAr
                            ? 'حتى 5 ملفات · 20 ميجابايت إجمالاً · PDF / صور / Word / Excel'
                            : 'Up to 5 files · 20 MB total · PDF / images / Word / Excel'}
                        </p>
                      </div>
                    )}

                    {/* Open-tracking toggle — injects a 1×1 pixel into each
                        recipient's HTML body so the admin can see who opened
                        the message. Visible in the "تتبع الإيميلات" tab. */}
                    {alsoEmail && (
                      <div className="mt-3 flex items-start gap-2 bg-emerald-50/60 border border-emerald-200 rounded-md px-3 py-2.5" data-testid="track-opens-row">
                        <Checkbox
                          id="track-opens"
                          checked={trackOpens}
                          onCheckedChange={(v) => setTrackOpens(!!v)}
                          className="mt-0.5"
                          data-testid="track-opens-cb"
                        />
                        <Label htmlFor="track-opens" className="text-xs text-emerald-900 cursor-pointer leading-snug">
                          <span className="font-semibold block">
                            {isAr ? 'تتبع فتح الإيميل' : 'Track email opens'}
                          </span>
                          <span className="text-emerald-800/80 text-[11px]">
                            {isAr
                              ? 'يضيف صورة مخفية لمعرفة من فتح الإيميل ومتى. الإحصائيات تظهر في تبويب «تتبع الإيميلات».'
                              : 'Adds a hidden pixel to track who opened the email and when. Stats appear in the "Email Tracking" tab.'}
                          </span>
                        </Label>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Audience selector */}
            <Card className="border-[#C4BFF0]" data-testid="audience-card">
              <CardHeader>
                <CardTitle className="text-[#655dc6] text-base flex items-center gap-2">
                  <Users size={20} weight="duotone" />
                  {isAr ? 'المستلمون' : 'Audience'}
                  <span className="text-xs font-normal text-[#655dc6]/60">
                    ({recipientCount} {isAr ? 'موظف' : 'employees'})
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  {[
                    { v: 'all',         label_ar: 'كل الموظفين',  label_en: 'All employees',     count: totalEmployees },
                    { v: 'departments', label_ar: 'أقسام محددة',  label_en: 'Specific depts',    count: departments.length },
                    { v: 'users',       label_ar: 'موظفون محددون', label_en: 'Specific users',    count: selectedIds.size },
                  ].map((opt) => (
                    <Button
                      key={opt.v}
                      variant={audience === opt.v ? 'default' : 'outline'}
                      onClick={() => setAudience(opt.v)}
                      className={audience === opt.v ? 'bg-[#655dc6] hover:bg-[#544cb0] text-white' : 'border-[#C4BFF0] text-[#655dc6]'}
                      data-testid={`audience-${opt.v}`}
                    >
                      {isAr ? opt.label_ar : opt.label_en}
                    </Button>
                  ))}
                </div>

                {audience !== 'all' && (
                  <div className="border border-[#C4BFF0] rounded-md max-h-[340px] overflow-y-auto bg-white">
                    {departments.map((d) => {
                      const expanded = expandedDepts.has(d.department);
                      const empIds = d.employees.map((e) => e.id);
                      const allDeptSelected = empIds.length > 0 && empIds.every((id) => selectedIds.has(id));
                      return (
                        <div key={d.department} className="border-b border-[#C4BFF0]/50 last:border-b-0">
                          <div className="flex items-center justify-between px-3 py-2 bg-[#F2F0FA]">
                            <button
                              onClick={() => toggleExpand(d.department)}
                              className="flex items-center gap-2 text-sm font-semibold text-[#655dc6]"
                              data-testid={`dept-toggle-${d.department}`}
                            >
                              {expanded ? <CaretDown size={14} /> : <CaretRight size={14} />}
                              {d.department} <span className="text-xs text-[#655dc6]/60">({d.employees.length})</span>
                            </button>
                            {audience === 'departments' ? (
                              <Checkbox
                                checked={selectedDepts.has(d.department)}
                                onCheckedChange={() => setSelectedDepts((p) => {
                                  const next = new Set(p);
                                  if (next.has(d.department)) next.delete(d.department); else next.add(d.department);
                                  return next;
                                })}
                                data-testid={`dept-cb-${d.department}`}
                              />
                            ) : (
                              <Checkbox
                                checked={allDeptSelected}
                                onCheckedChange={() => toggleDept(d.department)}
                                data-testid={`dept-all-cb-${d.department}`}
                              />
                            )}
                          </div>
                          {expanded && audience === 'users' && (
                            <ul className="py-1">
                              {d.employees.map((emp) => (
                                <li key={emp.id} className="flex items-center justify-between px-6 py-1 hover:bg-[#F8F7FB]">
                                  <span className="text-xs text-[#655dc6]/90">{emp.name}</span>
                                  <Checkbox
                                    checked={selectedIds.has(emp.id)}
                                    onCheckedChange={() => toggleEmployee(emp.id)}
                                    data-testid={`user-cb-${emp.id}`}
                                  />
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            <div className="flex justify-end">
              <Button
                onClick={handleSend}
                disabled={sending || !titleAr.trim() || recipientCount === 0}
                className="bg-[#655dc6] hover:bg-[#544cb0] text-white gap-2"
                data-testid="send-btn"
              >
                {sending ? <SpinnerGap size={16} className="animate-spin" /> : <PaperPlaneTilt size={16} weight="duotone" />}
                {sending
                  ? (isAr ? 'جاري الإرسال...' : 'Sending...')
                  : alsoEmail
                    ? (isAr ? `إرسال إلى ${recipientCount} موظف (داخلي + بريد)` : `Send to ${recipientCount} employee${recipientCount !== 1 ? 's' : ''} (in-app + email)`)
                    : (isAr ? `إرسال إلى ${recipientCount} موظف` : `Send to ${recipientCount} employees`)}
              </Button>
            </div>
          </TabsContent>

          {/* LOGS tab */}
          <TabsContent value="logs" className="space-y-4 mt-4">
            <Card className="border-[#C4BFF0]" data-testid="logs-card">
              <CardHeader>
                <CardTitle className="text-[#655dc6] text-base">
                  {isAr ? 'الإشعارات المرسلة' : 'Sent announcements'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loadingLogs ? (
                  <div className="flex justify-center py-8 text-[#655dc6]/70">
                    <SpinnerGap size={24} className="animate-spin" />
                  </div>
                ) : logs.length === 0 ? (
                  <p className="text-center py-8 text-[#655dc6]/60 text-sm">
                    {isAr ? 'لم يتم إرسال إشعارات بعد' : 'No announcements sent yet'}
                  </p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow className="border-[#C4BFF0]/50">
                        <TableHead className="text-start">{isAr ? 'العنوان' : 'Title'}</TableHead>
                        <TableHead className="text-start">{isAr ? 'الجمهور' : 'Audience'}</TableHead>
                        <TableHead className="text-center">{isAr ? 'المستلمون' : 'Recipients'}</TableHead>
                        <TableHead className="text-start">{isAr ? 'بواسطة' : 'By'}</TableHead>
                        <TableHead className="text-start">{isAr ? 'التاريخ' : 'When'}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {logs.map((l, idx) => (
                        <TableRow key={idx} className="border-[#C4BFF0]/30">
                          <TableCell className="text-[#655dc6] font-medium">{l.title_ar}</TableCell>
                          <TableCell className="text-xs text-[#655dc6]/80">
                            {l.audience === 'all'
                              ? (isAr ? 'كل الموظفين' : 'All')
                              : l.audience === 'departments'
                                ? (l.departments || []).join(', ')
                                : (isAr ? `${(l.user_ids || []).length} موظف محدد` : `${(l.user_ids || []).length} users`)}
                          </TableCell>
                          <TableCell className="text-center font-bold text-[#655dc6]">{l.recipients_count}</TableCell>
                          <TableCell className="text-xs">{l.created_by_name || '-'}</TableCell>
                          <TableCell className="text-xs text-[#655dc6]/60">{fmtKsa(l.created_at)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* EMAIL TRACKING tab — shows only those emails that were sent
              with the "track opens" checkbox enabled, so admins can see
              who actually opened the message and when. */}
          <TabsContent value="tracking" className="space-y-4 mt-4">
            <Card className="border-[#C4BFF0]" data-testid="tracking-card">
              <CardHeader className="flex flex-row items-center justify-between gap-2">
                <CardTitle className="text-[#655dc6] text-base">
                  {isAr ? 'تتبع فتح الإيميلات' : 'Email open tracking'}
                </CardTitle>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={async () => {
                      try {
                        const { data } = await axios.post(`${API}/notifications/tracking/reset-prefetch-opens`, {}, { withCredentials: true });
                        toast.success(isAr ? `تم تصحيح ${data.reset} سجل (فتحات مزيفة من معاينة العميل)` : `Reset ${data.reset} prefetch opens`);
                        fetchTracking();
                      } catch (e) {
                        toast.error(e.response?.data?.detail || (isAr ? 'فشل التصحيح' : 'Failed'));
                      }
                    }}
                    className="border-amber-300 text-amber-700 hover:bg-amber-50 gap-1.5 text-xs"
                    title={isAr ? 'يعيد ضبط أي «فتح» سُجِّل خلال أقل من دقيقة من الإرسال (Gmail/Yahoo يفتحون الصور تلقائياً عند الاستلام)' : 'Reset any "open" recorded within 60s of send'}
                    data-testid="reset-prefetch-btn"
                  >
                    {isAr ? 'تصحيح الفتحات المُسبقة' : 'Fix prefetch opens'}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={fetchTracking}
                    className="border-[#C4BFF0] text-[#655dc6] gap-1.5"
                    data-testid="refresh-tracking-btn"
                  >
                    <ClockClockwise size={14} weight="duotone" />
                    {isAr ? 'تحديث' : 'Refresh'}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {loadingTracking ? (
                  <div className="flex justify-center py-8 text-[#655dc6]/70">
                    <SpinnerGap size={24} className="animate-spin" />
                  </div>
                ) : tracking.length === 0 ? (
                  <div className="text-center py-10 text-[#655dc6]/60 text-sm space-y-2">
                    <p>{isAr ? 'لا توجد إيميلات مفعّل عليها التتبع بعد' : 'No tracked emails yet'}</p>
                    <p className="text-xs text-[#655dc6]/40">
                      {isAr
                        ? 'فعّل خيار «تتبع فتح الإيميل» أثناء إرسال إشعار جديد ثم عُد إلى هذا التبويب.'
                        : 'Enable "Track email opens" when sending a new announcement, then return here.'}
                    </p>
                  </div>
                ) : (
                  <>
                    {/* Summary KPIs — high-signal at a glance */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4" data-testid="tracking-kpis">
                      {(() => {
                        const total = tracking.length;
                        const opened = tracking.filter(t => t.opened).length;
                        const failed = tracking.filter(t => t.status === 'failed').length;
                        const rate = total ? Math.round((opened / total) * 100) : 0;
                        const cells = [
                          { label: isAr ? 'إجمالي المُرسل' : 'Total sent', value: total, tone: 'bg-[#F2F0FA] border-[#C4BFF0] text-[#655dc6]' },
                          { label: isAr ? 'مفتوحة' : 'Opened',           value: opened, tone: 'bg-emerald-50 border-emerald-200 text-emerald-800' },
                          { label: isAr ? 'لم تُفتح' : 'Unopened',        value: total - opened - failed, tone: 'bg-amber-50 border-amber-200 text-amber-800' },
                          { label: isAr ? 'نسبة الفتح' : 'Open rate',     value: `${rate}%`, tone: 'bg-sky-50 border-sky-200 text-sky-800' },
                        ];
                        return cells.map((c, i) => (
                          <div key={i} className={`border rounded-lg p-3 ${c.tone}`}>
                            <p className="text-xs opacity-80 mb-0.5">{c.label}</p>
                            <p className="text-xl font-semibold tabular-nums">{c.value}</p>
                          </div>
                        ));
                      })()}
                    </div>

                    <Table>
                      <TableHeader>
                        <TableRow className="border-[#C4BFF0]/50">
                          <TableHead className="text-start">{isAr ? 'الموظف' : 'Employee'}</TableHead>
                          <TableHead className="text-start">{isAr ? 'الموضوع' : 'Subject'}</TableHead>
                          <TableHead className="text-center">{isAr ? 'حالة الإرسال' : 'Send status'}</TableHead>
                          <TableHead className="text-center">{isAr ? 'الفتح' : 'Open'}</TableHead>
                          <TableHead className="text-center">{isAr ? 'عدد المرات' : 'Count'}</TableHead>
                          <TableHead className="text-start">{isAr ? 'أول فتح' : 'First opened'}</TableHead>
                          <TableHead className="text-start">{isAr ? 'تاريخ الإرسال' : 'Sent at'}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {tracking.map((t, idx) => (
                          <TableRow key={t.tracking_id || idx} className="border-[#C4BFF0]/30" data-testid={`tracking-row-${idx}`}>
                            <TableCell className="text-[#655dc6] font-medium">{t.employee_name || '-'}</TableCell>
                            <TableCell className="text-xs text-[#655dc6]/90 max-w-[280px] truncate" title={t.subject}>{t.subject}</TableCell>
                            <TableCell className="text-center">
                              {t.status === 'sent' ? (
                                <span className="inline-flex items-center gap-1 text-[11px] font-medium bg-emerald-100 text-emerald-800 border border-emerald-300 px-2 py-0.5 rounded-full">
                                  <CheckCircle size={11} weight="bold" />{isAr ? 'تم' : 'Sent'}
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 text-[11px] font-medium bg-rose-100 text-rose-800 border border-rose-300 px-2 py-0.5 rounded-full">
                                  <XCircle size={11} weight="bold" />{isAr ? 'فشل' : 'Failed'}
                                </span>
                              )}
                            </TableCell>
                            <TableCell className="text-center">
                              {t.opened ? (
                                <span className="inline-flex items-center gap-1 text-[11px] font-medium bg-emerald-100 text-emerald-800 border border-emerald-300 px-2 py-0.5 rounded-full" data-testid={`open-yes-${idx}`}>
                                  <CheckCircle size={11} weight="bold" />{isAr ? 'مفتوح' : 'Opened'}
                                </span>
                              ) : (
                                <span className="text-[11px] text-[#655dc6]/50">{isAr ? 'لم يُفتح' : 'Not yet'}</span>
                              )}
                            </TableCell>
                            <TableCell className="text-center tabular-nums text-xs text-[#655dc6]">
                              {t.open_count || 0}
                            </TableCell>
                            <TableCell className="text-xs text-[#655dc6]/70">
                              {fmtKsa(t.opened_at)}
                            </TableCell>
                            <TableCell className="text-xs text-[#655dc6]/60">
                              {fmtKsa(t.sent_at)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Auto-events footer */}
        <Card className="border-emerald-200 bg-emerald-50/30" data-testid="auto-events-card">
          <CardContent className="py-3 px-4">
            <p className="text-sm font-semibold text-emerald-800 flex items-center gap-2">
              <CheckCircle size={18} weight="duotone" />
              {isAr ? 'الإشعارات التلقائية' : 'Automatic notifications'}
            </p>
            <p className="text-xs text-emerald-700/80 mt-1">
              {isAr
                ? 'النظام يُرسل إشعارات تلقائية للموظفين عند: ✓ إصدار كشف الراتب  ✓ الموافقة/رفض طلب الإجازة. لا حاجة لإرسالها يدوياً.'
                : 'The system auto-notifies employees on: ✓ payslip issuance  ✓ leave approval/rejection. No manual action needed.'}
            </p>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
